import React, { useState, useEffect } from 'react';
import { useAuth } from '../../../context/AuthContext';
import { attendanceAPI } from '../../../services';
import { useApi, useAsyncAction } from '../../../hooks';
import {
  Button,
  Card,
  LoadingSpinner,
  Badge,
  ErrorMessage,
  SuccessMessage,
  StatsCard
} from '../../UI';
import { Clock, MapPin, CheckCircle2, XCircle, Calendar, TrendingUp, AlertTriangle } from 'lucide-react';
import AttendanceCalendar from '../AttendanceCalendar';
import BirthdayWidget from '../BirthdayWidget';

const EmployeeOverview = () => {
  const { user } = useAuth();
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');
  const [currentTime, setCurrentTime] = useState(new Date());
  
  const { data: todayData, loading: todayLoading, refetch: refetchToday } = useApi(
    attendanceAPI.getTodayAttendance,
    []
  );
  
  const { data: historyData, loading: historyLoading } = useApi(
    () => attendanceAPI.getMyHistory({
      month: new Date().getMonth() + 1,
      year: new Date().getFullYear()
    }),
    []
  );

  const { loading: punchLoading, execute: executePunch } = useAsyncAction();

  const todayAttendance = todayData?.attendance;
  const hasPunchedIn = todayData?.hasPunchedIn || false;
  const hasPunchedOut = todayData?.hasPunchedOut || false;
  const stats = historyData?.stats || {};

  useEffect(() => {
    const timer = setInterval(() => setCurrentTime(new Date()), 1000);
    return () => clearInterval(timer);
  }, []);

  const getCurrentLocation = () => {
    return new Promise((resolve, reject) => {
      if (!navigator.geolocation) {
        reject(new Error('Geolocation not supported'));
        return;
      }
      navigator.geolocation.getCurrentPosition(
        (position) => resolve({
          latitude: position.coords.latitude,
          longitude: position.coords.longitude,
        }),
        (error) => reject(error),
        { enableHighAccuracy: true, timeout: 10000 }
      );
    });
  };

  const handlePunch = async (type) => {
    try {
      setError('');
      const location = await getCurrentLocation();
      const result = await executePunch(
        type === 'in' ? attendanceAPI.punchIn : attendanceAPI.punchOut,
        { location }
      );
      if (result.success) {
        setSuccess(`Punch ${type} successful!`);
        refetchToday();
        setTimeout(() => setSuccess(''), 3000);
      } else {
        setError(result.error || `Failed to punch ${type}`);
      }
    } catch (err) {
      setError(err.message || 'Failed to get location');
    }
  };

  const formatTime = (date) => {
    if (!date) return '-';
    return new Date(date).toLocaleTimeString('en-IN', {
      hour: '2-digit',
      minute: '2-digit',
      hour12: true,
      timeZone: 'Asia/Kolkata'
    });
  };

  return (
    <div className="space-y-6">
      {error && <ErrorMessage message={error} onClose={() => setError('')} />}
      {success && <SuccessMessage message={success} onClose={() => setSuccess('')} />}

      {/* Welcome & Time */}
      <div className="bg-gradient-to-r from-blue-600 to-indigo-600 rounded-2xl p-6 text-white">
        <div className="flex flex-col md:flex-row justify-between items-start md:items-center gap-4">
          <div>
            <h2 className="text-2xl font-bold mb-1">Welcome, {user?.name}!</h2>
            <p className="text-blue-100">
              {currentTime.toLocaleDateString('en-IN', { weekday: 'long', year: 'numeric', month: 'long', day: 'numeric' })}
            </p>
          </div>
          <div className="text-right">
            <p className="text-4xl font-bold font-mono">
              {currentTime.toLocaleTimeString('en-IN', { hour: '2-digit', minute: '2-digit', second: '2-digit', timeZone: 'Asia/Kolkata' })}
            </p>
          </div>
        </div>
      </div>

      {/* Punch Card */}
      <div className="bg-white rounded-xl border border-gray-200 p-6">
        <h3 className="font-semibold text-gray-900 mb-4 flex items-center gap-2">
          <Clock className="w-5 h-5 text-blue-600" />
          Today's Attendance
        </h3>
        
        {todayLoading ? (
          <div className="flex justify-center py-8">
            <LoadingSpinner />
          </div>
        ) : (
          <div className="space-y-4">
            <div className="grid grid-cols-2 md:grid-cols-4 gap-4 text-center">
              <div className="p-4 bg-green-50 rounded-xl">
                <p className="text-sm text-gray-500 mb-1">Punch In</p>
                <p className="text-lg font-bold text-green-600">
                  {todayAttendance?.punchIn ? formatTime(todayAttendance.punchIn.time) : '--:--'}
                </p>
              </div>
              <div className="p-4 bg-red-50 rounded-xl">
                <p className="text-sm text-gray-500 mb-1">Punch Out</p>
                <p className="text-lg font-bold text-red-600">
                  {todayAttendance?.punchOut ? formatTime(todayAttendance.punchOut.time) : '--:--'}
                </p>
              </div>
              <div className="p-4 bg-blue-50 rounded-xl">
                <p className="text-sm text-gray-500 mb-1">Work Hours</p>
                <p className="text-lg font-bold text-blue-600">
                  {(() => {
                    const hours = todayAttendance?.workHours || (todayAttendance?.workDuration ? todayAttendance.workDuration / 60 : 0);
                    return hours && !isNaN(hours) ? `${Number(hours).toFixed(1)}h` : '0h';
                  })()}
                </p>
              </div>
              <div className="p-4 bg-purple-50 rounded-xl">
                <p className="text-sm text-gray-500 mb-1">Status</p>
                <Badge variant={todayAttendance?.status === 'present' ? 'success' : 'secondary'}>
                  {todayAttendance?.status || 'Not marked'}
                </Badge>
              </div>
            </div>

            {/* <div className="flex justify-center gap-4 pt-4">
              {!hasPunchedIn ? (
                <Button
                  onClick={() => handlePunch('in')}
                  loading={punchLoading}
                  className="flex items-center gap-2 bg-green-600 hover:bg-green-700"
                >
                  <CheckCircle2 className="w-5 h-5" />
                  Punch In
                </Button>
              ) : !hasPunchedOut ? (
                <Button
                  onClick={() => handlePunch('out')}
                  loading={punchLoading}
                  className="flex items-center gap-2 bg-red-600 hover:bg-red-700"
                >
                  <XCircle className="w-5 h-5" />
                  Punch Out
                </Button>
              ) : (
                <div className="text-center text-gray-500">
                  <CheckCircle2 className="w-8 h-8 text-green-500 mx-auto mb-2" />
                  <p>Attendance marked for today</p>
                </div>
              )}
            </div> */}
          </div>
        )}
      </div>

      {/* Monthly Stats */}
      <div className="grid grid-cols-2 lg:grid-cols-4 gap-4">
        <div className="bg-white rounded-xl border border-gray-200 p-4">
          <div className="flex items-center gap-3">
            <div className="w-10 h-10 rounded-lg bg-green-100 flex items-center justify-center">
              <CheckCircle2 className="w-5 h-5 text-green-600" />
            </div>
            <div>
              <p className="text-sm text-gray-500">Present Days</p>
              <p className="text-xl font-bold text-gray-900">{stats.presentDays || 0}</p>
            </div>
          </div>
        </div>
        <div className="bg-white rounded-xl border border-gray-200 p-4">
          <div className="flex items-center gap-3">
            <div className="w-10 h-10 rounded-lg bg-red-100 flex items-center justify-center">
              <XCircle className="w-5 h-5 text-red-600" />
            </div>
            <div>
              <p className="text-sm text-gray-500">Absent Days</p>
              <p className="text-xl font-bold text-gray-900">{stats.absentDays || 0}</p>
            </div>
          </div>
        </div>
        <div className="bg-white rounded-xl border border-gray-200 p-4">
          <div className="flex items-center gap-3">
            <div className="w-10 h-10 rounded-lg bg-yellow-100 flex items-center justify-center">
              <AlertTriangle className="w-5 h-5 text-yellow-600" />
            </div>
            <div>
              <p className="text-sm text-gray-500">Late Arrivals</p>
              <p className="text-xl font-bold text-gray-900">{stats.lateDays || 0}</p>
            </div>
          </div>
        </div>
        <div className="bg-white rounded-xl border border-gray-200 p-4">
          <div className="flex items-center gap-3">
            <div className="w-10 h-10 rounded-lg bg-blue-100 flex items-center justify-center">
              <TrendingUp className="w-5 h-5 text-blue-600" />
            </div>
            <div>
              <p className="text-sm text-gray-500">Avg Hours</p>
              <p className="text-xl font-bold text-gray-900">{stats.avgWorkHours && !isNaN(stats.avgWorkHours) ? Number(stats.avgWorkHours).toFixed(1) : 0}h</p>
            </div>
          </div>
        </div>
      </div>

      {/* Quick Links */}
      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
        <div className="lg:col-span-2 bg-white rounded-xl border border-gray-200 p-6">
          <h3 className="font-semibold text-gray-900 mb-4">Quick Actions</h3>
          <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
            <a href="/dashboard/attendance" className="flex flex-col items-center p-4 bg-blue-50 rounded-xl hover:bg-blue-100 transition-colors">
              <Calendar className="w-6 h-6 text-blue-600 mb-2" />
              <span className="text-sm font-medium text-blue-700">Attendance History</span>
            </a>
            <a href="/dashboard/leaves" className="flex flex-col items-center p-4 bg-green-50 rounded-xl hover:bg-green-100 transition-colors">
              <Calendar className="w-6 h-6 text-green-600 mb-2" />
              <span className="text-sm font-medium text-green-700">Apply Leave</span>
            </a>
            <a href="/dashboard/customers" className="flex flex-col items-center p-4 bg-purple-50 rounded-xl hover:bg-purple-100 transition-colors">
              <MapPin className="w-6 h-6 text-purple-600 mb-2" />
              <span className="text-sm font-medium text-purple-700">My Customers</span>
            </a>
            <a href="/dashboard/payroll" className="flex flex-col items-center p-4 bg-yellow-50 rounded-xl hover:bg-yellow-100 transition-colors">
              <Clock className="w-6 h-6 text-yellow-600 mb-2" />
              <span className="text-sm font-medium text-yellow-700">View Payslips</span>
            </a>
          </div>
        </div>

        {/* Birthday Widget */}
        <div className="lg:col-span-1">
          <BirthdayWidget />
        </div>
      </div>
    </div>
  );
};

export default EmployeeOverview;
